import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box, Typography, Card, CardContent, CardActions, Button,
  CircularProgress, IconButton, Avatar, Grid, Divider, Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import { LearnersBookevent, LearnersLiveSessions } from "../../../../helper/base_url";
import useNetworkStatus from "../../../../helper/NetworkStatus";
import { ShimmerSimpleGallery } from "react-shimmer-effects";

const CalendlyEvents = () => {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingUrl, setBookingUrl] = useState(null);
  const [error, setError] = useState(null);
  const { isOnline } = useNetworkStatus();

  // Convert minutes to readable time
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    let result = "";

    if (hours > 0) result += `${hours} hour${hours > 1 ? "s" : ""}`;
    if (mins > 0) result += ` ${mins} minute${mins > 1 ? "s" : ""}`;
    return result || "Less than 1 minute";
  };

  // Get mentors from backend
  const fetchMentors = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(LearnersLiveSessions, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMentors(response.data.data || []);
      //console.log("Mentors fetched successfully:", response.data.data);
    } catch (err) {
      //console.error("Error fetching mentors:", err);
      setError("Failed to load mentor events. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Book meeting
  const bookMeeting = async (mentorUsername) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        LearnersBookevent,
        { mentorUsername },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const link = response.data?.data?.url;
      if (link) {
        setBookingUrl(link);
      } else {
        throw new Error("Booking failed");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Booking failed. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Close iframe
  const closeBooking = () => {
    setBookingUrl(null);
  };

  useEffect(() => {
    fetchMentors();
  }, []);

  return (
    <div className="mylearningpaths">
    <Box sx={{ px: 6, py: 4 }}>
      {isOnline ? (
        bookingUrl ? (
          <Box sx={{ position: "relative", p: 2, borderRadius: 3, boxShadow: 4, bgcolor: "#fff" }}>
            <Typography variant="h5" fontWeight={700} color="#043885" mb={2}>
              <VideoCallIcon sx={{ mr: 1 }} />
              Book Your Session
            </Typography>
            <IconButton
              sx={{ position: "absolute", top : 12, right : 12 }}
              onClick={closeBooking}
              color="primary"
            >
              <CloseIcon />
            </IconButton>
            <iframe
              src={bookingUrl}
              width="100%"
              height="600px"
              title="Calendly Booking"
              style={{
                border: "none",
                marginTop : "10px",
                borderRadius: "12px",
                boxShadow: "0 0 10px rgba(0,0,0,0.08)",
              }}
            />
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                background: "#F0F6FF",
                borderRadius: 3,
                mb: 4,
                p:"10px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
              }}
              >
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#043885", mb: 1.5 }}
              >
              <EventIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                 Meet Our Data Science and AI/ML Mentors
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ mb: 1, color: "#333", fontWeight: 500 }}
              >
                Choose your mentor.
              </Typography>

               <Alert
              severity="info"
              icon={false}
              sx={{
                backgroundColor: "#E3F2FD",
                color: "#043885",
                fontSize: "15px",
                fontWeight: 500,
                mt: 2,
                mb: 2,
                maxWidth : 600,
                mx: "auto",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              You can book up to <strong>4 sessions</strong> per month. Once you click the <strong>Book Now</strong> button, it will count as one session used. Please click it only if you're ready to schedule your session.
            </Alert>
            </Box>
            {loading ? (
              <Box display="flex" justifyContent="center" mt={5}>
                <CircularProgress color="primary" />
              </Box>
            ) : error ? (
              <Typography color="error" align="center">{error}</Typography>
            ) : mentors.length === 0 ? (
              <Typography align="center">No mentor events found.</Typography>
            ) : (
              <Grid container spacing={4}>
                {mentors
                    .filter((mentor) =>
                        ["Book your Data Science Mentor.", "Book your Data Science Mentor", "Book Your AI&ML Mentor"].includes(mentor.calendlyEventName)
                    )
                  .map((mentor, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card
                        sx={{
                          borderRadius: 4,
                          boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                          backgroundColor: "#ffffff",
                          transition: "transform 0.3s ease",
                          marginTop :"20px",
                          "&:hover": {
                            transform: "translateY(-6px)",
                            boxShadow: "0 12px 30px rgba(4,56,133,0.15)",
                          },
                        }}
                      >
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Avatar sx={{ bgcolor: "#043885" }}>
                              {mentor.eventMentor?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600}>{mentor.eventMentor}</Typography>
                              {/* <Typography variant="caption" color="text.secondary">
                                IoT Mentor
                              </Typography> */}
                            </Box>
                          </Box>

                          <Divider sx={{ mb: 2 }} />

                          <Box display="flex" alignItems="center" mb={1}>
                            <EventIcon sx={{ color: "#1B6EF4", mr: 1 }} />
                            <Typography variant="body2">
                              {mentor.calendlyEventName}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center">
                            <AccessTimeIcon sx={{ color: "#1B6EF4", mr: 1 }} />
                            <Typography variant="body2">
                              Duration: {formatDuration(mentor.calendlyEventType?.duration || 30)}
                            </Typography>
                          </Box>
                        </CardContent>

                        <CardActions sx={{ p: 2, pt: 0 }}>
                            <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => bookMeeting(mentor.calendlyUsername)}
                            startIcon={<VideoCallIcon />}
                            sx={{
                              borderColor: "#043885",
                              color: "#043885",
                              fontWeight: 600,
                              fontSize: "15px",
                              borderRadius: 2,
                              py: 1.3,
                              textTransform: "none",
                              transition: "0.3s ease",
                              "&:hover": {
                                backgroundColor: "#043885",
                                color: "#fff",
                                borderColor: "#043885",
                                boxShadow: "0 4px 12px rgba(4,56,133,0.3)",
                              },
                            }}
                          >
                            Book Now
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            )}
          </Box>
        )
     ) : (
      <Box>
        <ShimmerSimpleGallery
          card
          imageHeight={180}
          row={3}
          col={3}
          gap={20}
        />
      </Box>
    )}
    </Box>
    </div>
  );
};

export default CalendlyEvents;
